import { useEffect, useRef, type RefObject } from 'react';
import { useLocation } from 'wouter';

/**
 * Move focus to the main landmark on client-side navigation (V2-018C-1).
 *
 * A full page load resets focus to the top of the document. Client-side routing
 * does not: the URL and the content change while focus stays wherever the
 * activated link was, so a keyboard visitor who follows a navigation link then
 * tabs forward continues from inside the old page's navigation, and a screen
 * reader announces nothing at all. Moving focus to the labelled main region is
 * the standard remedy and announces the new page as a side effect.
 *
 * The first render is deliberately skipped. Stealing focus on arrival would
 * scroll a visitor past the header before they have done anything.
 *
 * **This hook must be owned above the shell boundary.** Its previous-location
 * state lives in a ref, and a ref only survives while the component that
 * declared it stays mounted. Called from inside `ExperienceShell` or
 * `ApplicationShell` — which are different component types, so switching
 * between them unmounts one and mounts the other — the state resets and the
 * first-render branch suppresses the focus move on exactly the navigations that
 * matter most: a marketing page into the assessment runner, or a result back
 * out. One owner, above the switch, is the whole point.
 */
export function useRouteFocus(mainRef: RefObject<HTMLElement | null>): void {
  const [location] = useLocation();
  const previous = useRef<string | null>(null);

  useEffect(() => {
    if (previous.current === null) {
      previous.current = location;
      return;
    }
    if (previous.current === location) return;
    previous.current = location;
    mainRef.current?.focus();
  }, [location, mainRef]);
}
